"use client";

import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type TraitData = {
  name: string;
  label: string;
  description: string;
};

const traits: TraitData[] = [
  {
    name: "mw_me",
    label: "MW - ME",
    description: "Maternal Weight - Maternal Effect",
  },
  {
    name: "bw_de",
    label: "BW - DE",
    description: "Birth Weight - Direct Effect",
  },
  {
    name: "ww_de",
    label: "WW - DE",
    description: "Weaning Weight - Direct Effect",
  },
  {
    name: "aw_de",
    label: "AW - DE",
    description: "Annual Weight - Direct Effect",
  },
  {
    name: "yw_de",
    label: "YW - DE",
    description: "Yearling Weight - Direct Effect",
  },
  {
    name: "sc365",
    label: "SC365",
    description: "Scrotal Perimeter at 365 days",
  },
  {
    name: "sc450",
    label: "SC450",
    description: "Scrotal Perimeter at 450 days",
  },
  {
    name: "bs",
    label: "B.S",
    description: "Body Structure",
  },
  {
    name: "prec",
    label: "PREc",
    description: "Precocity",
  },
  {
    name: "musc",
    label: "MUSC",
    description: "Muscularity",
  },
  {
    name: "rea",
    label: "REA",
    description: "Rib Eye Area",
  },
  {
    name: "finishing",
    label: "Finishing",
    description: "Finishing",
  },
  {
    name: "mar",
    label: "MAR",
    description: "Marbling",
  },
  {
    name: "iabcz",
    label: "iABCZ",
    description: "iABCZ Index",
  },
];

export function GeneticBreedingValuesForm() {
  const { control } = useFormContext();

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-center mb-6">
        Genetic Breeding Values
      </h2>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[180px]">Trait</TableHead>
            <TableHead>EPD</TableHead>
            <TableHead>ACC.</TableHead>
            <TableHead>DECA</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {traits.map((trait) => (
            <TableRow key={trait.name}>
              <TableCell className="font-medium">
                <div>
                  <div>{trait.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {trait.description}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <FormField
                  control={control}
                  name={`${trait.name}_epd`}
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="EPD"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            )
                          }
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TableCell>
              <TableCell>
                <FormField
                  control={control}
                  name={`${trait.name}_acc`}
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="ACC"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            )
                          }
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TableCell>
              <TableCell>
                <FormField
                  control={control}
                  name={`${trait.name}_deca`}
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="DECA"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            )
                          }
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Card className="p-4 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Expected Progeny Differences (EPDs) predict how future offspring of an
          animal are expected to perform relative to the offspring of other
          animals. Accuracy (ACC) indicates reliability of the EPD value. DECA
          is a standardized index used in genetic evaluation systems.
        </p>
      </Card>
    </div>
  );
}
